import { Button, Text } from '@/shared/ui';

export interface OfferExit {
  key: 'pack' | 'plan';
  title: string;
  caption: string;
  ctaLabel: string;
  onCta: () => void;
  emphasis: 'primary' | 'secondary';
  disabled?: boolean;
}

interface Props {
  exits: OfferExit[];
}

/**
 * The ways out of a quota shortage, side by side.
 *
 * The order is not negotiable: resolving this cycle comes before changing
 * plan. When a small one-off purchase solves the month, leading with a much
 * larger recurring commitment costs the screen its credibility, and every
 * quota warning after it gets ignored.
 *
 * Renders nothing when there is no exit to offer, so the block above degrades
 * to its headline rather than framing an empty card.
 *
 * @param props - Component props.
 * @returns The exits, or null.
 */
export function OfferExits({ exits }: Props) {
  if (exits.length === 0) return null;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: 'var(--space-3)',
    }}>
      {exits.map((exit) => (
        <div
          key={exit.key}
          className="flex flex-col gap-(--space-2-5) min-w-0"
          data-testid={`offer-exit-${exit.key}`}
          style={{
            padding: 'var(--space-3-5)',
            border: `1px solid ${exit.emphasis === 'primary'
              ? 'var(--border-medium)' : 'var(--border-subtle)'}`,
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface-primary)',
          }}
        >
          <div className="flex flex-col gap-(--space-0-5)">
            <Text component="span" style={{
              fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-bold)',
              color: 'var(--text-primary)',
            }}>
              {exit.title}
            </Text>
            <Text component="span" style={{
              fontSize: 'var(--text-meta)', color: 'var(--text-tertiary-soft)',
              lineHeight: 'var(--leading-normal)',
            }}>
              {exit.caption}
            </Text>
          </div>
          <Button
            size="sm"
            variant={exit.emphasis === 'primary' ? 'solid' : 'outlined'}
            disabled={exit.disabled}
            onClick={exit.onCta}
            data-testid={`offer-cta-${exit.key}`}
          >
            {exit.ctaLabel}
          </Button>
        </div>
      ))}
    </div>
  );
}
